import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  DatePicker,
  FileUploader,
  Button,
  Loader,
  LoaderSizes,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { getValidationHistorySagaAction } from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import WhitelabelSubmissionsList from "../WhitelabelSubmissionsList/index";
import WhitelabelSubmissionDetail from "../WhitelabelSubmissionDetail/index";
import "./WhitelabelTab.scss";

interface WhitelabelTabProps {
  isHIDUser: boolean;
}

type WhitelabelViewState = "form" | "list" | "detail";

interface WhitelabelFormData {
  appNameForSdk: string;
  appNameForBackend: string;
  endCustomerName: string;
  devTeamSize: string;
  devTeamLocation: string;
  applicationVersion: string;
  accessControlSystem: string;
  hidOrigoApiVersion: string;
  programmingLanguage: string;
  hostingEnvironment: string;
  developmentLanguagePlatform: string;
  postValidationDeployment: string;
  plannedProductionLaunchDate: Date | null;
  endUserUseCases: string;
  sequenceFlowDiagram: any | null;
}

const VALIDATED_FULL_APPROVAL = "Validated - Full approval";

const WhitelabelTab: React.FC<WhitelabelTabProps> = ({ isHIDUser }) => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const dispatch = useAppDispatch();
  const { historyData, status } = useAppSelector(
    (state) => state.validation_documentation
  );

  const [viewState, setViewState] = useState<WhitelabelViewState>("form");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

  const [formData, setFormData] = useState<WhitelabelFormData>({
    appNameForSdk: "",
    appNameForBackend: "",
    endCustomerName: "",
    devTeamSize: "",
    devTeamLocation: "",
    applicationVersion: "",
    accessControlSystem: "",
    hidOrigoApiVersion: "",
    programmingLanguage: "",
    hostingEnvironment: "",
    developmentLanguagePlatform: "",
    postValidationDeployment: "",
    plannedProductionLaunchDate: null,
    endUserUseCases: "",
    sequenceFlowDiagram: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fetch validation history so we can check if "Apply for whitelabel" should be enabled
  useEffect(() => {
    if (integrationId) {
      dispatch(getValidationHistorySagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // Enable "Apply for whitelabel" only when the first item in history has "Validated - Full approval"
  const hasFullApproval = useMemo(
    () =>
      historyData.length > 0 &&
      historyData[0].validation_status === VALIDATED_FULL_APPROVAL,
    [historyData]
  );

  const handleInputChange = (field: keyof WhitelabelFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = useMemo(() => {
    return (
      formData.appNameForSdk.trim() !== "" &&
      formData.appNameForBackend.trim() !== "" &&
      formData.endCustomerName.trim() !== "" &&
      formData.devTeamSize.trim() !== "" &&
      formData.devTeamLocation.trim() !== "" &&
      formData.applicationVersion.trim() !== "" &&
      formData.accessControlSystem.trim() !== "" &&
      formData.hidOrigoApiVersion.trim() !== "" &&
      formData.programmingLanguage.trim() !== "" &&
      formData.developmentLanguagePlatform.trim() !== "" &&
      formData.postValidationDeployment.trim() !== "" &&
      formData.plannedProductionLaunchDate !== null &&
      formData.endUserUseCases.trim() !== "" &&
      formData.sequenceFlowDiagram !== null
    );
  }, [formData]);

  const handleSubmit = async () => {
    if (!isFormValid || isLoading) return;

    setIsLoading(true);
    // TODO: API integration
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handleCancel = () => {
    window.history.back();
  };

  const handleHistoryClick = () => {
    setViewState("list");
  };

  const handleBackToForm = () => {
    setViewState("form");
    setSelectedSubmissionId(null);
  };

  const handleViewSubmission = (submissionId: string) => {
    setSelectedSubmissionId(submissionId);
    setViewState("detail");
  };

  const handleBackToList = () => {
    setViewState("list");
    setSelectedSubmissionId(null);
  };

  const handleSave = () => {
    console.log("Saving whitelabel form data...");
    // TODO: Implement save functionality
  };

  // Render submissions list view
  if (viewState === "list") {
    return (
      <WhitelabelSubmissionsList
        isHIDUser={isHIDUser}
        onBackToForm={handleBackToForm}
        onViewSubmission={handleViewSubmission}
      />
    );
  }

  // Render submission detail view
  if (viewState === "detail" && selectedSubmissionId) {
    return (
      <WhitelabelSubmissionDetail
        isHIDUser={isHIDUser}
        submissionId={selectedSubmissionId}
        onBackToList={handleBackToList}
      />
    );
  }

  // Render form view (default)
  return (
    <div className="whitelabel-tab-container">
      <div className="whitelabel-form-wrapper">
        <div className="whitelabel-header">
          <h2>Apply for white labelling</h2>
          <Button
            variant="flat"
            label="White label Submission History"
            normalCase={true}
            onClick={handleHistoryClick}
          />
        </div>

        {isSubmitted && (
          <div className="success-message">
            <Iconography icon="circleCheck" size={IconographySizes.Small} />
            <span>Whitelabel application has been submitted.</span>
          </div>
        )}

        <div className="form-note">
          <span>Note:</span> All fields marked * below are mandatory.
        </div>

        <div className="whitelabel-form-container">
          <div className="form-row">
            <div className="form-field">
              <TextField
                label="App Name for SDK"
                id="appNameForSdk"
                name="appNameForSdk"
                placeholder="Enter app name for SDK"
                value={formData.appNameForSdk}
                theme="dark"
                onChange={(e) => handleInputChange("appNameForSdk", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="App Name for backend System/IAM System/headend system (API)"
                id="appNameForBackend"
                name="appNameForBackend"
                placeholder="Enter app name for backend system"
                value={formData.appNameForBackend}
                theme="dark"
                onChange={(e) => handleInputChange("appNameForBackend", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="End Customer Name"
                id="endCustomerName"
                name="endCustomerName"
                placeholder="Enter end customer name"
                value={formData.endCustomerName}
                theme="dark"
                onChange={(e) => handleInputChange("endCustomerName", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Dev team size"
                id="devTeamSize"
                name="devTeamSize"
                placeholder="Enter dev team size"
                value={formData.devTeamSize}
                theme="dark"
                onChange={(e) => handleInputChange("devTeamSize", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Dev team location"
                id="devTeamLocation"
                name="devTeamLocation"
                placeholder="Enter dev team location"
                value={formData.devTeamLocation}
                theme="dark"
                onChange={(e) => handleInputChange("devTeamLocation", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Application version"
                id="applicationVersion"
                name="applicationVersion"
                placeholder="Select app version"
                value={formData.applicationVersion}
                theme="dark"
                onChange={(e) => handleInputChange("applicationVersion", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Access Control System"
                id="accessControlSystem"
                name="accessControlSystem"
                placeholder="Select access control system"
                value={formData.accessControlSystem}
                theme="dark"
                onChange={(e) => handleInputChange("accessControlSystem", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Which version of HID Origo API currently using?"
                id="hidOrigoApiVersion"
                name="hidOrigoApiVersion"
                placeholder="Select version"
                value={formData.hidOrigoApiVersion}
                theme="dark"
                onChange={(e) => handleInputChange("hidOrigoApiVersion", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Programming Language used for API Integration"
                id="programmingLanguage"
                name="programmingLanguage"
                placeholder="Programming language used..."
                value={formData.programmingLanguage}
                theme="dark"
                onChange={(e) => handleInputChange("programmingLanguage", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Hosting Environment for API integration? (For example: AWS, Azure, On-Premises)"
                id="hostingEnvironment"
                name="hostingEnvironment"
                placeholder="Hosting environment"
                value={formData.hostingEnvironment}
                theme="dark"
                onChange={(e) => handleInputChange("hostingEnvironment", e.currentTarget.value)}
                isRequired={false}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="Development language/platform used in application developed? (For example: JavaScript, etc)"
                id="developmentLanguagePlatform"
                name="developmentLanguagePlatform"
                placeholder="Dev language/platform used..."
                value={formData.developmentLanguagePlatform}
                theme="dark"
                onChange={(e) => handleInputChange("developmentLanguagePlatform", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="How will the application be deployed to the users post validation?"
                id="postValidationDeployment"
                name="postValidationDeployment"
                placeholder="Post validation deployment"
                value={formData.postValidationDeployment}
                theme="dark"
                onChange={(e) => handleInputChange("postValidationDeployment", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <DatePicker
                onChange={(date) => handleInputChange("plannedProductionLaunchDate", date)}
                id="plannedProductionLaunchDate"
                name="plannedProductionLaunchDate"
                label="Planned Production Launch Date"
                format="dd-MMM-yyyy"
                placeholder="Select"
                theme="dark"
                value={formData.plannedProductionLaunchDate}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <TextField
                label="What are the end user use case(s) intended for this application?"
                id="endUserUseCases"
                name="endUserUseCases"
                placeholder="End user use case"
                value={formData.endUserUseCases}
                theme="dark"
                onChange={(e) => handleInputChange("endUserUseCases", e.currentTarget.value)}
                isRequired={true}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field-file-upload">
              <label className="file-upload-label">
                Please provide/upload the sequence flow diagram <span>*</span>
              </label>
              <div className="file-input-container">
                <FileUploader
                  buttonConfig={{ label: "Browse" }}
                  isMultiple={false}
                  onUpload={(e, data) => {
                    if (data && data.length > 0) {
                      handleInputChange("sequenceFlowDiagram", data[0].fileDetails);
                    } else {
                      handleInputChange("sequenceFlowDiagram", null);
                    }
                  }}
                  onRemove={() => handleInputChange("sequenceFlowDiagram", null)}
                  validateConfig={[
                    {
                      maxFileSize: {
                        maxSizeInMb: 4,
                        message: "File exceeds 4MB size limit.",
                      },
                    },
                  ]}
                />
                <div className="file-upload-note">Maximum upload file size: 4MB.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="whitelabel-footer">
        <Button
          variant="flat"
          label="Cancel"
          normalCase={true}
          onClick={handleCancel}
          disabled={isLoading}
        />
        <div className="footer-right-actions">
          <Button
            variant="flat"
            label="Save"
            normalCase={true}
            onClick={handleSave}
            disabled={isLoading}
          />
          <Button
            variant="primary"
            label="Apply for whitelabel"
            normalCase={true}
            onClick={handleSubmit}
            disabled={
              !hasFullApproval ||
              status.historyLoading ||
              !isFormValid ||
              isLoading
            }
            icon={isLoading ? <Loader variant={LoaderSizes.small} /> : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default WhitelabelTab;

